<script setup lang="ts">
import { useStaticImage } from "@/hooks";
import { computed } from "vue";

const props = withDefaults(
  defineProps<{
    overflowY?: string;
    direction?: string;
    justify?: string;
    align?: string;
    wrap?: string;
    height?: string;
    width?: string;
    flex?: string;
    padding?: string;
    margin?: string;
    border?: string;
    borderWidth?: string;
    borderStyle?: string;
    borderColor?: string;
    borderRadius?: string;
    boxShadow?: boolean;
    background?: string;
    backgroundImage?: string;
    extStyle?: Record<string, any>;
    flexLayout?: boolean;
  }>(),
  {
    overflowY: "auto",
    direction: "column",
    justify: "start",
    align: "stretch",
    wrap: "wrap",
    height: "auto",
    width: "auto",
    flex: "none",
    padding: "5px",
    margin: "0",
    border: "none",
    borderRadius: "0",
    flexLayout: true,
  }
);

const emit = defineEmits(["click"]);

const { imageUrl } = useStaticImage(computed(() => props.backgroundImage));

const style = computed(() => {
  let ret: Record<string, string> = {
    overflowY: props.overflowY,
    height: props.height,
    width: props.width,
    flex: props.flex,
    padding: props.padding,
    margin: props.margin,
    borderRadius: props.borderRadius,
    boxShadow: props.boxShadow ? "0px 0px 12px rgba(0, 0, 0, .12)" : "none",
  };

  if (props.background) {
    ret.background = props.background;
  }

  if (props.border !== "none" && props.border !== "") {
    ret.border = props.border;
  }

  if (props.borderWidth) {
    ret.borderWidth = props.borderWidth;
  }

  if (props.borderStyle) {
    ret.borderStyle = props.borderStyle;
  }

  if (props.borderColor) {
    ret.borderColor = props.borderColor;
  }

  if (imageUrl.value) {
    ret = {
      ...ret,
      backgroundImage: `url(${imageUrl.value})`,
      backgroundRepeat: "no-repeat",
      backgroundSize: "cover",
    };
  }
  return ret;
});

const innerStyle = computed(() =>
  props.extStyle ? { ...style.value, ...props.extStyle } : style.value
);
</script>
<template>
  <div
    class="mp-panel"
    :class="
      flexLayout
        ? [
            'flex',
            `flex-direction-${direction}`,
            `justify-content-${justify}`,
            `align-items-${align}`,
            `flex-wrap-${wrap}`,
          ]
        : ''
    "
    :style="innerStyle"
    @click="emit('click', $event)"
  >
    <slot />
  </div>
</template>
<style lang="scss">
.mp-panel {
  display: block;
}

.flex {
  display: flex;
}

$flexDirections: row, column, row-reverse, column-reverse;
@each $flexDirection in $flexDirections {
  .flex-direction-#{$flexDirection} {
    flex-direction: $flexDirection;
  }
}

$flexWraps: nowrap, wrap, wrap-reverse;
@each $flexWrap in $flexWraps {
  .flex-wrap-#{$flexWrap} {
    flex-wrap: $flexWrap;
  }
}

$justifyContents: (
  "start": flex-start,
  "end": flex-end,
  "center": center,
  "space-between": space-between,
  "space-around": space-around,
  "space-evenly": space-evenly,
);
@each $name, $value in $justifyContents {
  .justify-content-#{$name} {
    justify-content: $value;
  }
}

$alignItems: (
  "top": flex-start,
  "bottom": flex-end,
  "middle": center,
  "baseline": baseline,
  "stretch": stretch,
);
@each $name, $value in $alignItems {
  .align-items-#{$name} {
    align-items: $value;
  }
}
</style>
